import { Asset } from 'nftx.js';
import { useCallback, useMemo, useState } from 'react';

export type TraitFilters = Record<string, string[]>;
export type Filter = {
  key: string;
  value: string[];
  count: number;
  options: {
    value: string;
    count: number;
  }[];
};

const doesAssetHaveTraits = (
  ignoreKey: string,
  traitFilters: Record<string, string[]>,
  asset: Asset
) => {
  return Object.entries(traitFilters).every(([k, v]) => {
    if (k === ignoreKey) {
      return true;
    }
    if (!v.length) {
      return true;
    }
    if (v.includes(asset.traits[k])) {
      return true;
    }
    return false;
  });
};
const groupFiltersByTrait = (
  assets: Asset[],
  traitFilters: Record<string, string[]>
) => {
  const matrix: Record<string, Record<string, number>> = {};

  assets.forEach((asset) => {
    Object.entries(asset.traits).forEach(([key, value]) => {
      const options = (matrix[key] = matrix[key] ?? {});

      const currentCount = options[value] ?? 0;
      const included = doesAssetHaveTraits(key, traitFilters, asset);

      if (included) {
        options[value] = currentCount + 1;
      }
    });
  });

  return matrix;
};
const getFiltersFromTraits = (
  matrix: Record<string, Record<string, number>>,
  traitFilters: Record<string, string[]>
) => {
  return Object.entries(matrix).map(([key, optionCounts]) => {
    const options = Object.entries(optionCounts).map(([value, count]) => ({
      value,
      count,
    }));
    const value = traitFilters[key] ?? [];
    const count = value.reduce((total, v) => {
      const count = optionCounts[v] ?? 0;
      return total + count;
    }, 0);

    return {
      key,
      value,
      count,
      options,
    };
  });
};

const useTraitFilters = ({
  assets,
  initialFilters = {},
}: {
  initialFilters?: TraitFilters;
  assets: Asset[] | undefined;
}) => {
  const [traitFilters, setTraitFilters] =
    useState<TraitFilters>(initialFilters);
  const toggleTraitFilter = useCallback((key: string, value: string) => {
    setTraitFilters((traitFilters) => {
      const traitFilter = [...(traitFilters[key] ?? [])];
      const i = traitFilter.indexOf(value);
      if (i >= 0) {
        traitFilter.splice(i, 1);
      } else {
        traitFilter.push(value);
      }
      return {
        ...traitFilters,
        [key]: traitFilter,
      };
    });
  }, []);

  const filters = useMemo((): Filter[] => {
    const matrix = groupFiltersByTrait(assets || [], traitFilters);
    const filters = getFiltersFromTraits(matrix, traitFilters);
    return filters;
  }, [assets, traitFilters]);

  const canFilter = filters.length > 0;
  const hasFilters = useMemo(
    () => filters.some((f) => f.value.length > 0 && f.count > 0),
    [filters]
  );

  const filteredAssets = useMemo(() => {
    if (!hasFilters) {
      return assets;
    }
    return assets?.filter((asset) => {
      return filters.every((filter) => {
        if (!filter.value.length) {
          return true;
        }
        if (filter.value.includes(asset.traits[filter.key])) {
          return true;
        }
        return false;
      });
    });
  }, [assets, filters, hasFilters]);

  return {
    traitFilters,
    setTraitFilters,
    toggleTraitFilter,
    filters,
    canFilter,
    hasFilters,
    filteredAssets,
  };
};

export default useTraitFilters;
